<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import VivacrmVisionAPI from 'dashboard/api/integrations/vivacrmVision';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const VISION_TAG_IDS_KEY = 'vivacrm_vision_tag_ids';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const contactGetter = useMapGetter('contacts/getContact');
const contactData = computed(() => contactGetter.value(props.contactId) || props.contact);

const selectedTagIds = computed(() => {
  const raw = contactData.value?.custom_attributes?.[VISION_TAG_IDS_KEY];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
});

const tags = ref([]);
const tagsLoading = ref(false);
const ticketsData = ref(null);
const ticketsLoading = ref(false);
const ticketsError = ref(null);
const dropdownOpen = ref(false);
const tagSearchQuery = ref('');

const availableTagsSorted = computed(() =>
  [...tags.value].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  )
);

const filteredTags = computed(() => {
  const q = tagSearchQuery.value.trim().toLowerCase();
  if (!q) return availableTagsSorted.value;
  return availableTagsSorted.value.filter(tag =>
    (tag.name || '').toLowerCase().includes(q)
  );
});

const selectedTags = computed(() =>
  availableTagsSorted.value.filter(tag => selectedTagIds.value.includes(tag.id))
);

const updateContactTagIds = async nextIds => {
  const customAttributes = {
    ...(contactData.value?.custom_attributes || {}),
    [VISION_TAG_IDS_KEY]: nextIds,
  };
  try {
    await store.dispatch('contacts/update', {
      id: props.contactId,
      customAttributes,
    });
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS'));
  } catch (err) {
    const msg = err?.response?.data?.message || t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR');
    useAlert(msg);
  }
};

const toggleTag = tagId => {
  dropdownOpen.value = false;
  const current = selectedTagIds.value;
  const next = current.includes(tagId)
    ? current.filter(id => id !== tagId)
    : [...current, tagId];
  updateContactTagIds(next);
};

const loadTags = async () => {
  tagsLoading.value = true;
  try {
    const { data } = await VivacrmVisionAPI.getTags();
    const list = Array.isArray(data) ? data : [];
    tags.value = list;
    store.commit('integrations/SET_VIVACRM_VISION_TAGS', list);
  } catch {
    tags.value = [];
  } finally {
    tagsLoading.value = false;
  }
};

const loadTickets = async () => {
  if (!selectedTagIds.value.length) {
    ticketsData.value = null;
    return;
  }
  ticketsLoading.value = true;
  ticketsError.value = null;
  try {
    const { data } = await VivacrmVisionAPI.getTickets({
      tagIds: selectedTagIds.value,
      pageSize: 20,
    });
    ticketsData.value = data;
  } catch (err) {
    ticketsError.value = err?.response?.data?.error || t('CONVERSATION_SIDEBAR.VIVACRM_VISION.SELECT_TAG_TO_SEE_TASKS');
    ticketsData.value = null;
  } finally {
    ticketsLoading.value = false;
  }
};

const projectShortKey = computed(() => ticketsData.value?.project_short_key || '');
const ticketsList = computed(() => {
  const list = ticketsData.value?.tickets || [];
  const withDue = list.filter(t => t.due_date);
  const withoutDue = list.filter(t => !t.due_date);
  const sortByDate = (a, b) => new Date(a.due_date) - new Date(b.due_date);
  const sortByStatus = (a, b) => (a.status_summary?.display_order ?? 0) - (b.status_summary?.display_order ?? 0);
  return [
    ...withDue.sort((a, b) => sortByDate(a, b) || sortByStatus(a, b)),
    ...withoutDue.sort(sortByStatus),
  ];
});

const ticketUrl = ticket =>
  `https://vision.vivacrm.ru/projects?tp=${projectShortKey.value}-${ticket.ticket_number_in_project}`;

watch(selectedTagIds, () => {
  loadTickets();
}, { immediate: true });

onMounted(() => {
  loadTags();
});
</script>

<template>
  <div class="flex flex-col gap-4 px-2 py-2">
    <div v-on-clickaway="() => (dropdownOpen = false)" class="relative">
      <p class="mb-2 text-xs font-medium uppercase tracking-wide text-n-slate-11">
        {{ $t('CONVERSATION_SIDEBAR.VIVACRM_VISION.TAGS_LABEL') }}
      </p>
      <div v-if="tagsLoading" class="flex justify-center py-4">
        <Spinner />
      </div>
      <template v-else>
        <button
          type="button"
          class="flex w-full items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-left text-sm text-n-slate-12 outline-none transition-colors hover:bg-n-slate-2"
          @click="dropdownOpen = !dropdownOpen"
        >
          <span class="min-w-0 truncate text-n-slate-11">
            {{ selectedTags.length ? $t('CONVERSATION_SIDEBAR.VIVACRM_VISION.TAGS_SELECTED', { count: selectedTags.length }) : $t('CONVERSATION_SIDEBAR.VIVACRM_VISION.TAGS_PLACEHOLDER') }}
          </span>
          <fluent-icon
            :icon="dropdownOpen ? 'chevron-up' : 'chevron-down'"
            size="14"
            class="shrink-0 text-n-slate-11"
          />
        </button>
        <div
          v-if="dropdownOpen"
          class="absolute left-0 right-0 top-full z-20 mt-1 max-h-[280px] overflow-hidden rounded-lg border border-n-weak bg-n-solid-2 shadow-lg outline outline-1 outline-n-weak"
          @click.stop
        >
          <div class="sticky top-0 flex items-center gap-2 border-b border-n-weak bg-n-solid-2 px-2 py-2">
            <fluent-icon icon="search" size="14" class="shrink-0 text-n-slate-11" />
            <input
              v-model="tagSearchQuery"
              type="search"
              :placeholder="$t('CONVERSATION_SIDEBAR.VIVACRM_VISION.SEARCH_TAGS')"
              class="min-w-0 flex-1 bg-transparent text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10"
            />
          </div>
          <div class="max-h-[220px] overflow-y-auto py-1">
            <button
              v-for="tag in filteredTags"
              :key="tag.id"
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-n-slate-2"
              :class="{ 'bg-n-slate-2': selectedTagIds.includes(tag.id) }"
              @click="toggleTag(tag.id)"
            >
              <span
                class="shrink-0 rounded px-1.5 py-0.5 text-xs font-medium"
                :style="{
                  backgroundColor: tag.color ? `${tag.color}20` : 'var(--n-slate-3)',
                  color: tag.color || 'var(--n-slate-12)',
                }"
              >
                {{ tag.name }}
              </span>
              <fluent-icon
                v-if="selectedTagIds.includes(tag.id)"
                icon="checkmark"
                size="14"
                class="ml-auto shrink-0 text-n-slate-11"
              />
            </button>
            <p
              v-if="!filteredTags.length"
              class="px-3 py-4 text-center text-sm text-n-slate-11"
            >
              {{ $t('CONVERSATION_SIDEBAR.VIVACRM_VISION.NO_MATCH') }}
            </p>
          </div>
        </div>
        <div v-if="selectedTags.length" class="mt-2 flex flex-wrap gap-1.5">
          <button
            v-for="tag in selectedTags"
            :key="tag.id"
            type="button"
            class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-offset-1 ring-n-weak transition-opacity hover:opacity-90"
            :style="{
              backgroundColor: tag.color ? `${tag.color}20` : 'var(--n-slate-3)',
              color: tag.color || 'var(--n-slate-12)',
            }"
            @click="toggleTag(tag.id)"
          >
            {{ tag.name }} ×
          </button>
        </div>
        <span v-if="!tags.length" class="mt-2 block text-sm text-n-slate-11">
          {{ $t('CONVERSATION_SIDEBAR.VIVACRM_VISION.NO_TAGS') }}
        </span>
      </template>
    </div>

    <div>
      <div class="mb-2 flex items-center justify-between gap-2">
        <p class="text-xs font-medium uppercase tracking-wide text-n-slate-11">
          {{ $t('CONVERSATION_SIDEBAR.VIVACRM_VISION.TASKS_IN_PROGRESS') }}
        </p>
        <NextButton
          v-if="selectedTagIds.length"
          xs
          ghost
          slate
          :icon="ticketsLoading ? undefined : 'i-lucide-refresh-cw'"
          :label="$t('CONVERSATION_SIDEBAR.VIVACRM_VISION.REFRESH')"
          :is-loading="ticketsLoading"
          @click="loadTickets"
        />
      </div>
      <template v-if="!selectedTagIds.length">
        <p class="text-sm text-n-slate-11">
          {{ $t('CONVERSATION_SIDEBAR.VIVACRM_VISION.SELECT_TAG_TO_SEE_TASKS') }}
        </p>
      </template>
      <template v-else>
        <div v-if="ticketsLoading && !ticketsData" class="flex justify-center py-4">
          <Spinner />
        </div>
        <p v-else-if="ticketsError" class="text-sm text-n-ruby-11">
          {{ ticketsError }}
        </p>
        <p v-else-if="!ticketsLoading && ticketsData && !ticketsList.length" class="text-sm text-n-slate-11">
          {{ $t('CONVERSATION_SIDEBAR.VIVACRM_VISION.NO_TASKS') }}
        </p>
        <div v-else-if="ticketsData && ticketsList.length" class="max-h-[320px] space-y-3 overflow-y-auto">
          <a
            v-for="ticket in ticketsList"
            :key="ticket.id"
            :href="ticketUrl(ticket)"
            target="_blank"
            rel="noopener noreferrer"
            class="block rounded-lg border border-n-weak bg-n-surface-1 p-3 text-left transition-colors hover:bg-n-slate-2"
          >
            <div class="flex items-start justify-between gap-2">
              <span
                class="shrink-0 rounded px-1.5 py-0.5 text-xs font-medium"
                :style="{
                  backgroundColor: ticket.status_summary?.color_bg || 'var(--n-slate-3)',
                  color: ticket.status_summary?.color || 'var(--n-slate-12)',
                }"
              >
                {{ ticket.status_summary?.name || '—' }}
              </span>
              <span
                v-if="ticket.due_date"
                class="text-xs text-n-slate-11"
              >
                {{ new Date(ticket.due_date).toLocaleDateString() }}
              </span>
            </div>
            <p class="mt-1.5 line-clamp-2 text-sm font-medium text-n-slate-12">
              {{ ticket.name }}
            </p>
            <p
              v-if="ticket.last_comment_summary?.body || ticket.description"
              class="mt-1 line-clamp-2 text-xs text-n-slate-11"
            >
              {{ (ticket.last_comment_summary?.body || ticket.description || '').slice(0, 120) }}{{ (ticket.last_comment_summary?.body || ticket.description || '').length > 120 ? '…' : '' }}
            </p>
          </a>
        </div>
      </template>
    </div>
  </div>
</template>
